import type { BoundingBox } from 'framer-motion';
import type { PointerEvent, ReactNode } from 'react';
import { motion, useDragControls, useMotionValue } from 'framer-motion';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../cn';
import usePortal from '../hooks/usePortal';
import Button from '../Input2/Button';
import { General, Layout } from '../StrokeIcons';

export interface FloatingWidgetProps {
  children: ReactNode;
  /** Renders the X in the drag-handle strip when provided. */
  onClose?: () => void;
  /** Tooltip for the X (e.g. "End call and close" while a call is live). */
  closeTooltip?: string;
  className?: string;
  'data-test'?: string;
}

/**
 * A non-modal, draggable floating card portaled to `document.body`. Unlike `Popup` there is no
 * backdrop and no `popupShown` body class — the page underneath stays fully visible and
 * interactive. Dragging starts only from the top handle strip (`dragListener={false}`), so the
 * card body's buttons never turn a press-and-slip into a drag.
 *
 * The fullscreen `pointer-events-none` wrapper is the box the card is clamped to. Its bounds are
 * measured from the DOM at each drag start and handed to framer-motion as pixels, not as a ref:
 * with a ref, framer-motion computes constraints from the card position it measured at mount (stale
 * for a portaled card, which mounts before it's laid out) and re-places the card on every window
 * resize from them — an undragged card jumped to mid-screen or the top-left corner, and drags
 * weren't clamped. An undragged card has no transform and rides the CSS anchor through resizes.
 * After an extreme viewport shrink a dragged card may sit partially offscreen until the next drag
 * re-clamps it — acceptable, the handle anchors to the card's top edge and stays reachable.
 */
export function FloatingWidget({
  children,
  onClose,
  closeTooltip,
  className,
  'data-test': dataTest,
}: FloatingWidgetProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [dragBounds, setDragBounds] = useState<Partial<BoundingBox>>({});
  const dragControls = useDragControls();

  const startDrag = (e: PointerEvent<HTMLDivElement>) => {
    const box = boxRef.current?.getBoundingClientRect();
    const card = cardRef.current?.getBoundingClientRect();
    // Bounds are in drag-offset space: how far x/y may move from where the card sits now.
    if (box != null && card != null) {
      setDragBounds({
        left: x.get() - (card.left - box.left),
        right: x.get() + (box.right - card.right),
        top: y.get() - (card.top - box.top),
        bottom: y.get() + (box.bottom - card.bottom),
      });
    }
    dragControls.start(e);
  };
  const target = usePortal('poseidon-floating-portal');

  return createPortal(
    // z-9000 keeps real modals (Popup at z-9998) stacked above the widget. The flex box centers
    // the card horizontally and anchors it to the bottom edge (with p-4 breathing room) — its
    // default "middle bottom" resting spot. Dragging then applies a transform on top of that
    // flow position, so the card can still be moved anywhere and stays clamped to this box.
    <div ref={boxRef} className="pointer-events-none fixed inset-0 z-9000 flex items-end justify-center p-4">
      <motion.div
        ref={cardRef}
        style={{ x, y }}
        drag
        dragListener={false}
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={dragBounds}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          'pointer-events-auto flex flex-col overflow-hidden rounded-xl',
          'bg-white shadow-2xl ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/15',
          className,
        )}
        data-test={dataTest}>
        <div
          onPointerDown={startDrag}
          data-test="floating-widget-handle"
          // touch-none is required: without it the browser claims touch moves for scrolling and
          // the drag never starts on touch devices.
          className="flex cursor-grab touch-none items-center justify-between gap-2 pt-1 pr-1 pl-3 select-none active:cursor-grabbing">
          <Layout.GridDotsBlank className="size-4 shrink-0 text-gray-300 dark:text-gray-600" />
          {onClose && (
            <Button
              text
              size="sm"
              icon={<General.XMark />}
              tooltip={closeTooltip}
              aria-label={closeTooltip ?? 'Close'}
              // Keep a press on the X from starting a drag on the handle strip.
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onClose}
              data-test="floating-widget-close"
            />
          )}
        </div>
        {children}
      </motion.div>
    </div>,
    target,
  );
}
